import { useMatch } from 'react-router-dom';
import { Cable, Cast, Wifi, WifiOff, Clock } from 'lucide-react';
import clsx from 'clsx';
import { useTransport, describeTransport } from '@/store/transport';

/**
 * Topbar pill that shows the *active* transport for the current
 * device (USB / Wi-Fi / 离线 / 等待中).
 *
 * Active-device resolution: the SPA's URL is the source of truth —
 * `/devices/:serial/*`. When we're on a list / dashboard page the
 * badge collapses to a tiny "无设备" hint instead of broadcasting a
 * stale serial. The store indexes by both primary serial AND
 * transport aliases, so a bookmark against the old USB serial still
 * resolves after the wireless line was attached.
 *
 * Real-time updates come from `useTransport` (WS-pushed
 * `transport-change` events). No polling here — that's the store's
 * job; the badge just re-renders when its slice changes.
 */
export function TopbarTransportBadge(): JSX.Element | null {
  // Match any path that nests a `:serial` segment. Covers
  // /devices/:serial, /devices/:serial/mirror, /devices/:serial/logs, …
  const match = useMatch('/devices/:serial/*');
  const serial = match?.params.serial;
  const entry = useTransport((s) => (serial ? s.bySerial[serial] : undefined));
  if (!serial) {
    return null;
  }
  const desc = describeTransport(entry);
  const Icon =
    desc.tone === 'usb'
      ? Cable
      : desc.tone === 'wireless'
        ? Wifi
        : desc.tone === 'tcpip'
          ? Cast
          : desc.tone === 'offline'
            ? WifiOff
            : Clock;
  const detail =
    entry?.activeTransport && entry.activeTransport.serial !== serial
      ? `当前路由: ${entry.activeTransport.serial}`
      : undefined;
  const titleParts = [
    `当前传输方式：${desc.label}`,
    entry?.transports && entry.transports.length > 0
      ? `已注册线路：${entry.transports
          .map((t) => `${transportLabel(t.kind)}(${t.serial})`)
          .join(' / ')}`
      : '未发现可用线路',
    detail,
    // Spell out the "mirror only" caveat in the tooltip so the user
    // knows scrcpy works but ws command / control / logcat are
    // blocked until they enable USB or wireless debugging.
    desc.tone === 'tcpip'
      ? 'TCP-IP 镜像备线只可用于 scrcpy 镜像；其他 adb 命令（control / logcat / ws command / push）请启用 USB 或无线调试'
      : undefined,
  ].filter(Boolean) as string[];
  return (
    <div
      className={clsx(
        'inline-flex h-8 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium',
        desc.tone === 'usb' &&
          'border-emerald-500/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
        desc.tone === 'wireless' &&
          'border-sky-500/50 bg-sky-500/10 text-sky-700 dark:text-sky-300',
        desc.tone === 'tcpip' &&
          'border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-300',
        desc.tone === 'offline' &&
          'border-rose-500/50 bg-rose-500/10 text-rose-700 dark:text-rose-300',
        desc.tone === 'unknown' && 'border-line bg-bg-soft text-ink-muted'
      )}
      title={titleParts.join('\n')}
    >
      <Icon className="h-3.5 w-3.5" />
      <span>{desc.short}</span>
    </div>
  );
}

function transportLabel(kind: string): string {
  if (kind === 'usb') return 'USB';
  if (kind === 'wireless') return 'Wi-Fi（无线调试）';
  if (kind === 'tcpip') return 'TCP-IP 镜像备线';
  return kind;
}
